/**
 * «Обзор района» на каноне — та же разметка K2, что у фикстурной ветки
 * (`overviewParts.tsx`), другие данные (Решение 10: замена реализации, а не
 * переписывание экрана).
 *
 * Откуда что:
 * * циклы, культуры, работы активного цикла, карантин, массивы и хозяйства
 *   для кадра карты — наборы прототипа через адаптеры канона
 *   (`data-source/api/*`): сервер считает, адаптер только называет;
 * * земля, массивы, структура посевов, очередь внимания и пробелы —
 *   `/overview/district`;
 * * вода активного цикла — `/season/picture` (тот же ответ, что у адаптера
 *   работ, из общей памяти запросов);
 * * число хозяйств района — `page.total` списка `/actors`.
 *
 * Чего у канона нет, блок K2 показывает как «нет данных» с причиной: вида
 * пункта внимания и даты «наблюдается с», счёта контуров по культурам,
 * кандидатов геометрии, охвата воды и районного журнала обследований.
 */

import { useMemo } from 'react';
import { Building2, LandPlot, ListChecks, MapPin, Sprout } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { KpiCard } from '../ui/KpiCard';
import { Card, Grid, Stack } from '../ui/Card';
import { Disclosure } from '../ui/Disclosure';
import { EmptyState } from '../ui/EmptyState';
import { EvidenceMark } from '../ui/EvidenceMark';
import { DataGate } from './parts';
import { usePageTrail } from '../shell/ShellContext';
import { useTrackData } from '../data/useTrackData';
import { FILES } from '../data/loader';
import {
  countOf,
  cycleTitle,
  dateLong,
  ha,
  haUnit,
  m3,
  num,
  WORDS,
} from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { COMMON } from '../../i18n/common';
import { DENSITY } from '../lib/levels';
import type {
  Crop,
  Cycle,
  Massif,
  Meta,
  QuarantineFile,
  Work,
} from '../data/types';
import { api, type QueryParams } from '../../api/client';
import { useRequest } from '../../api/useRequest';
import { useAnalysisContext } from '../../analysis/context';
import { once, paramsKey } from '../data-source/api/adapter';
import { microhaToHa, noData } from '../data-source/api/compare';
import { reportShareLabel, reportShareNote, reportShareValue } from '../lib/reportShare';
import { attentionLine, canonQueueItem, landOf, volumeM3 } from '../data-source/api/overview';
import {
  ActiveCycle,
  closedCycleFacts,
  ClosedCycleRow,
  CropsBreakdown,
  MapFrameCard,
  levelCaption,
  MassifTable,
  QueueItem,
  ReadyRow,
  type CropSlice,
  type MassifRow,
} from './overviewParts';
import './pages.css';
import './overview.css';

const m = defineMessages({
  districtName: { id: 'district.overviewApi.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в заголовке и хлебных крошках страницы «Район»; географическое имя — транслитерация' },
  title: { id: 'district.overviewApi.title', defaultMessage: 'Район {district}', description: 'Заголовок страницы «Район»; {district} — название района' },
  purposeDate: {
    id: 'district.overviewApi.purposeDate',
    defaultMessage: 'Что происходит на земле района на дату среза {date}. Всё считается в массивах, контурах, хозяйствах и гектарах района — доли других уровней сюда не заходят.',
    description: 'Подзаголовок страницы «Район»; {date} — полная дата среза («26 августа 2026»)',
  },
  purposeNoDate: {
    id: 'district.overviewApi.purposeNoDate',
    defaultMessage: 'Что происходит на земле района на дату среза. Всё считается в массивах, контурах, хозяйствах и гектарах района — доли других уровней сюда не заходят.',
    description: 'Подзаголовок страницы «Район», когда дата среза неизвестна',
  },
  markDemo: { id: 'district.overviewApi.mark.demo', defaultMessage: 'Срез включает синтетику демонстрации', description: 'Метка доверия справа в заголовке: в срез включены демонстрационные данные' },
  markFact: { id: 'district.overviewApi.mark.fact', defaultMessage: 'Канон без синтетики', description: 'Метка доверия справа в заголовке: данные из основной базы без демонстрационных' },
  markCanon: { id: 'district.overviewApi.mark.canon', defaultMessage: 'Канон', description: 'Метка доверия у строки готовности данных: данные из основной базы; одно слово' },
  markDerived: { id: 'district.overviewApi.mark.derived', defaultMessage: 'Производное', description: 'Метка доверия: значение получено расчётом из других данных' },
  noReasonServer: { id: 'district.overviewApi.noServerAnswer', defaultMessage: 'сервер не ответил', description: 'Причина по умолчанию, когда сервер не вернул данные и текста ошибки нет; строчными' },

  kpiActive: { id: 'district.overviewApi.kpi.active', defaultMessage: 'Активный агроцикл', description: 'Название главного KPI: площадь идущего агроцикла (хоким, менеджер); коротко' },
  kpiHaLand: { id: 'district.overviewApi.kpi.haLand', defaultMessage: 'га земли', description: 'Единица у числа гектаров уникальной земли (не «га работ»); коротко' },
  kpiHa: { id: 'district.overviewApi.kpi.ha', defaultMessage: 'га', description: 'Единица «гектары» у числа земли района; коротко' },
  kpiActiveNote: { id: 'district.overviewApi.kpi.activeNote', defaultMessage: '{title} · этап: {stage}', description: 'Подпись KPI «Активный агроцикл»; {title} — «Хлопок 2026», {stage} — название этапа или «не назван»' },
  stageUnnamed: { id: 'district.overviewApi.kpi.stageUnnamed', defaultMessage: 'не назван', description: 'Название этапа, когда источник его не назвал; встаёт после «этап:»; строчными' },
  kpiActiveLoading: { id: 'district.overviewApi.kpi.activeLoading', defaultMessage: 'агроцикл ещё загружается', description: 'Подпись KPI «Активный агроцикл», пока данные грузятся; строчными' },
  kpiActiveNone: { id: 'district.overviewApi.kpi.activeNone', defaultMessage: 'в срезе нет агроцикла со статусом «идёт»', description: 'Подпись KPI «Активный агроцикл», когда нет идущего агроцикла; строчными' },
  kpiPictureLoading: { id: 'district.overviewApi.kpi.pictureLoading', defaultMessage: 'картина сезона загружается', description: 'Подпись KPI «Выполнено», пока грузится картина сезона; строчными' },
  kpiPictureUnread: { id: 'district.overviewApi.kpi.pictureUnread', defaultMessage: 'картина сезона не прочитана', description: 'Подпись KPI «Выполнено», когда картина сезона не получена; строчными' },
  kpiLand: { id: 'district.overviewApi.kpi.land', defaultMessage: 'Земля района', description: 'Название KPI: вся сельхозземля района; коротко' },
  kpiLandNote: { id: 'district.overviewApi.kpi.landNote', defaultMessage: '{massifs} района', description: 'Подпись KPI «Земля района»; {massifs} — «5 массивов»' },
  kpiLandNoRow: { id: 'district.overviewApi.kpi.landNoRow', defaultMessage: 'с/х земля: {noData} — строки в обзоре района нет', description: 'Подпись KPI «Земля района», когда в данных нет строки сельхозземли; {noData} — «нет данных»' },
  kpiOverviewLoading: { id: 'district.overviewApi.kpi.overviewLoading', defaultMessage: 'обзор района загружается', description: 'Подпись KPI, пока грузится обзор района; строчными' },
  kpiOverviewUnreadNumber: { id: 'district.overviewApi.kpi.overviewUnreadNumber', defaultMessage: 'обзор района не прочитан — число не выдумывается', description: 'Подпись KPI, когда обзор района не получен: число не подставляется; строчными' },
  kpiOverviewUnread: { id: 'district.overviewApi.kpi.overviewUnread', defaultMessage: 'обзор района не прочитан', description: 'Короткая подпись KPI, когда обзор района не получен; строчными' },
  kpiFarms: { id: 'district.overviewApi.kpi.farms', defaultMessage: 'Хозяйства', description: 'Название KPI: число хозяйств района; коротко' },
  kpiFarmsLoading: { id: 'district.overviewApi.kpi.farmsLoading', defaultMessage: 'реестр хозяйств загружается', description: 'Подпись KPI «Хозяйства», пока грузится реестр; строчными' },
  kpiFarmsUnread: { id: 'district.overviewApi.kpi.farmsUnread', defaultMessage: 'реестр хозяйств не прочитан', description: 'Подпись KPI «Хозяйства», когда реестр не получен; строчными' },
  kpiFarmsNote: { id: 'district.overviewApi.kpi.farmsNote', defaultMessage: 'в реестре основной базы', description: 'Подпись KPI «Хозяйства»: откуда число; строчными' },
  kpiContours: { id: 'district.overviewApi.kpi.contours', defaultMessage: 'Контуры на карте', description: 'Название KPI: сколько контуров реестра нарисовано на карте; коротко' },
  kpiContoursNote: {
    id: 'district.overviewApi.kpi.contoursNote',
    defaultMessage: 'из {count, plural, one {# контура} few {# контуров} many {# контуров} other {# контура}} реестра',
    description: 'Подпись KPI «Контуры на карте»: из скольких контуров реестра; {count} — всего контуров',
  },

  cardActive: { id: 'district.overviewApi.card.active', defaultMessage: 'Активный агроцикл', description: 'Заголовок карточки идущего агроцикла на странице «Район»' },
  cardActiveAside: { id: 'district.overviewApi.card.activeAside', defaultMessage: '{works} в агроцикле', description: 'Справа в заголовке карточки агроцикла; {works} — «40 работ»' },
  gateActive: { id: 'district.overviewApi.gate.active', defaultMessage: 'карточка активного агроцикла', description: 'Что не загрузилось (строчными): подставляется в сообщение о том, что блок ждёт данные' },
  activeNoneTitle: { id: 'district.overviewApi.active.noneTitle', defaultMessage: 'Активного агроцикла нет', description: 'Заголовок пустого состояния карточки идущего агроцикла' },
  statusesUnread: { id: 'district.overviewApi.active.statusesUnread', defaultMessage: 'статусы агроциклов не прочитаны: {reason}', description: 'Пояснение в пустом состоянии карточки агроцикла; {reason} — причина' },
  statusesLoading: { id: 'district.overviewApi.active.statusesLoading', defaultMessage: 'статусы агроциклов ещё загружаются', description: 'Пояснение в пустом состоянии карточки агроцикла, пока грузятся статусы; строчными' },
  activeNoneReason: { id: 'district.overviewApi.active.noneReason', defaultMessage: 'в срезе не нашлось агроцикла со статусом «идёт»: либо сезон закрыт, либо следующий ещё не заведён', description: 'Пояснение в пустом состоянии «Активного агроцикла нет»; строчными' },
  cardClosed: { id: 'district.overviewApi.card.closed', defaultMessage: 'Закрытые агроциклы', description: 'Заголовок карточки со списком закрытых агроциклов' },
  closedNoneTitle: { id: 'district.overviewApi.closed.noneTitle', defaultMessage: 'Закрытых агроциклов нет', description: 'Заголовок пустого состояния карточки закрытых агроциклов' },
  closedNoneReadyReason: { id: 'district.overviewApi.closed.noneReady', defaultMessage: 'в срезе только идущие агроциклы — истории пока не накопилось', description: 'Пояснение в пустом состоянии: список прочитан, закрытых нет; строчными' },
  closedNoneUnreadReason: { id: 'district.overviewApi.closed.noneUnread', defaultMessage: 'список агроциклов не прочитан', description: 'Пояснение в пустом состоянии: список агроциклов не получен; строчными' },

  cardQueue: { id: 'district.overviewApi.card.queue', defaultMessage: 'Очередь внимания', description: 'Заголовок карточки с очередью позиций, на которые стоит обратить внимание (хоким, менеджер)' },
  queueAside: { id: 'district.overviewApi.queue.aside', defaultMessage: 'верхние {top} из {total}', description: 'Справа в заголовке очереди внимания; {top} — сколько показано, {total} — всего позиций' },
  queueLoading: { id: 'district.overviewApi.queue.loading', defaultMessage: 'Загружаем данные среза…', description: 'Заглушка в карточке очереди внимания, пока грузятся данные среза' },
  queueUnreadTitle: { id: 'district.overviewApi.queue.unreadTitle', defaultMessage: 'Очередь не прочитана', description: 'Заголовок пустого состояния очереди внимания, когда обзор района не получен' },
  queueUnreadReason: { id: 'district.overviewApi.queue.unreadReason', defaultMessage: 'обзор района не получен: {reason}', description: 'Пояснение в пустом состоянии «Очередь не прочитана»; {reason} — причина' },
  queueEmptyTitle: { id: 'district.overviewApi.queue.emptyTitle', defaultMessage: 'Очередь пуста', description: 'Заголовок пустого состояния очереди внимания' },
  queueEmptyReason: { id: 'district.overviewApi.queue.emptyReason', defaultMessage: 'сервер не вернул ни одной позиции по этому срезу; это не значит, что вопросов нет — значит, ранжировать было нечего', description: 'Пояснение в пустом состоянии «Очередь пуста»; строчными' },
  queueNote: {
    id: 'district.overviewApi.queue.note',
    defaultMessage: 'Порядок задан рангом сервера. Клиент его не пересчитывает и своих сводных баллов не изобретает. Вида позиции и даты «наблюдается с» основная база не отдаёт: значок нейтральный, дата — «{noData}»; ссылка ведёт в раздел разбора.',
    description: 'Пояснение под очередью внимания; {noData} — «нет данных»',
  },
  queueRest: { id: 'district.overviewApi.queue.rest', defaultMessage: 'Остальные позиции', description: 'Заголовок раскрывающегося блока с остальными позициями очереди' },
  queueRestSummary: {
    id: 'district.overviewApi.queue.restSummary',
    defaultMessage: 'ещё {count, plural, one {# позиция} few {# позиции} many {# позиций} other {# позиции}}',
    description: 'Краткая подпись свёрнутого блока остальных позиций очереди; {count} — число позиций',
  },

  geoNone: { id: 'district.overviewApi.geo.none', defaultMessage: 'Разбивку по происхождению геометрии показать нечем: обзор района не прочитан ({reason}).', description: 'Пояснение справа от карты, когда нет данных о геометрии; {reason} — причина' },
  geoSome: {
    id: 'district.overviewApi.geo.some',
    defaultMessage:
      'Геометрией закрыты {accepted} из {total, plural, one {# контура} few {# контуров} many {# контуров} other {# контура}}. Кандидатов: {noData} — основная база не различает принятую геометрию и кандидата.',
    description: 'Пояснение справа от карты о геометрии; {accepted} — число контуров с геометрией (строка), {total} — всего контуров, {noData} — «нет данных»',
  },
  cropsMapEmpty: { id: 'district.overviewApi.crops.mapEmpty', defaultMessage: 'В структуре посевов сезона нет строк по культурам с площадью: цвет полигонов сейчас говорит о пробеле в данных, а не о посеве.', description: 'Пояснение справа от карты, когда нет культур с площадью' },
  cropsMapUnread: { id: 'district.overviewApi.crops.mapUnread', defaultMessage: 'Структура посевов не прочитана: {reason}.', description: 'Пояснение справа от карты, когда структура посевов не получена; {reason} — причина' },

  discCrops: { id: 'district.overviewApi.disc.crops', defaultMessage: 'Культуры 2026', description: 'Заголовок раскрывающегося блока с разбивкой площади по культурам' },
  discCropsSummary: { id: 'district.overviewApi.disc.cropsSummary', defaultMessage: '{crops}, {ha} га размещено', description: 'Краткая подпись свёрнутого блока культур; {crops} — «5 культур», {ha} — гектары размещённой земли' },
  discUnread: { id: 'district.overviewApi.disc.unread', defaultMessage: 'разбивка не прочитана', description: 'Краткая подпись свёрнутого блока культур, когда данные не получены; строчными' },
  cropsUnreadTitle: { id: 'district.overviewApi.crops.unreadTitle', defaultMessage: 'Разбивка не прочитана', description: 'Заголовок пустого состояния разбивки по культурам, когда данные не получены' },
  cropsUnreadReason: { id: 'district.overviewApi.crops.unreadReason', defaultMessage: 'структура посевов ждёт обзор района: {reason}', description: 'Пояснение в пустом состоянии разбивки по культурам; {reason} — причина' },
  cropsEmptyReason: { id: 'district.overviewApi.crops.emptyReason', defaultMessage: 'в структуре посевов сезона нет строк по культурам с площадью — это пробел в данных, а не нулевой посев', description: 'Пояснение в пустой разбивке по культурам; строчными' },
  cropsContoursNote: {
    id: 'district.overviewApi.crops.contoursNote',
    defaultMessage: 'Счёт контуров по культурам обзор района не отдаёт — в колонке «{noData}». Площадь — из структуры посевов сезона {season}.',
    description: 'Пояснение под разбивкой по культурам; {noData} — «нет данных», {season} — название сезона («2026»)',
  },
  cropsNoArea: {
    id: 'district.overviewApi.crops.noArea',
    defaultMessage: 'Ещё у {crops} площадь не названа — это пробел в данных, а не ноль.',
    description: 'Пояснение под разбивкой по культурам; {crops} — «2 культур» (у скольких культур нет площади)',
  },
  cropsMismatch: {
    id: 'district.overviewApi.crops.mismatch',
    defaultMessage: 'У {count} размещений сумма по культурам не сходится с заявленным итогом (расхождение {diff}).',
    description: 'Пояснение под разбивкой по культурам; {count} — число размещений, {diff} — расхождение в гектарах с единицей',
  },
  discMassifs: { id: 'district.overviewApi.disc.massifs', defaultMessage: 'Массивы', description: 'Заголовок раскрывающегося блока с таблицей массивов' },
  discMassifsSummary: { id: 'district.overviewApi.disc.massifsSummary', defaultMessage: '{massifs}, {ha} га', description: 'Краткая подпись свёрнутого блока массивов; {massifs} — «5 массивов», {ha} — суммарная площадь' },
  discMassifsSummaryNoArea: {
    id: 'district.overviewApi.disc.massifsSummaryNoArea',
    defaultMessage: '{massifs}, {ha} га + площадь не названа у {missing}',
    description: 'Краткая подпись свёрнутого блока массивов, когда у части массивов площадь не названа; {missing} — у скольких массивов',
  },
  discListUnread: { id: 'district.overviewApi.disc.listUnread', defaultMessage: 'список не прочитан', description: 'Краткая подпись свёрнутого блока, когда список не получен; строчными' },
  massifsEmptyReady: { id: 'district.overviewApi.massifs.emptyReady', defaultMessage: 'обзор района прочитан, но массивов в нём нет — сравнивать нечего', description: 'Пояснение в пустой таблице массивов; строчными' },
  massifsEmptyUnread: { id: 'district.overviewApi.massifs.emptyUnread', defaultMessage: 'обзор района не прочитан: {reason}', description: 'Пояснение в пустой таблице массивов, когда обзор не получен; {reason} — причина' },
  discReady: { id: 'district.overviewApi.disc.ready', defaultMessage: 'Готовность данных', description: 'Заголовок раскрывающегося блока: какие данные есть и откуда они' },
  discReadySummary: { id: 'district.overviewApi.disc.readySummary', defaultMessage: 'геометрия принята у {accepted} из {total}', description: 'Краткая подпись свёрнутого блока готовности данных; {accepted}/{total} — числа контуров' },
  discCountUnknown: { id: 'district.overviewApi.disc.countUnknown', defaultMessage: 'счёт неизвестен', description: 'Краткая подпись свёрнутого блока, когда счёта нет; строчными' },

  readyRegistry: { id: 'district.overviewApi.ready.registry', defaultMessage: 'Реестр контуров и площади', description: 'Название строки в «Готовности данных»: реестр контуров' },
  readyRegistryFact: { id: 'district.overviewApi.ready.registryFact', defaultMessage: '{contours} · {area} · {massifs}', description: 'Факты строки «Реестр контуров и площади»; {contours} — «300 контуров», {area} — «1 200,00 га» или «с/х земля: нет данных», {massifs} — «5 массивов»' },
  readyLandNoData: { id: 'district.overviewApi.ready.landNoData', defaultMessage: 'с/х земля: {noData}', description: 'Вместо площади сельхозземли в строке реестра; {noData} — «нет данных»' },
  readyOverviewUnread: { id: 'district.overviewApi.ready.overviewUnread', defaultMessage: 'обзор района не прочитан: {reason}', description: 'Факт строки готовности, когда обзор района не получен; {reason} — причина' },
  readyGeometry: { id: 'district.overviewApi.ready.geometry', defaultMessage: 'Геометрия контуров', description: 'Название строки в «Готовности данных»: геометрия контуров' },
  readyGeometryFact: {
    id: 'district.overviewApi.ready.geometryFact',
    defaultMessage: 'принята у {accepted} из {total}; кандидатов: {noData} (основная база их не различает); без геометрии {none}',
    description: 'Факты строки «Геометрия контуров»; {accepted}/{total}/{none} — числа контуров, {noData} — «нет данных»',
  },
  readyFarms: { id: 'district.overviewApi.ready.farms', defaultMessage: 'Хозяйства и их земля', description: 'Название строки в «Готовности данных»: хозяйства' },
  readyFarmsFact: { id: 'district.overviewApi.ready.farmsFact', defaultMessage: '{farms} в реестре; глава хозяйства: {noData} — основная база его не хранит', description: 'Факт строки «Хозяйства и их земля»; {farms} — «264 хозяйства», {noData} — «нет данных»' },
  readyFarmsUnread: { id: 'district.overviewApi.ready.farmsUnread', defaultMessage: 'счёт неизвестен: реестр хозяйств не прочитан', description: 'Факт строки «Хозяйства и их земля», когда реестр не получен' },
  readyCycles: { id: 'district.overviewApi.ready.cycles', defaultMessage: 'Агроциклы и работы', description: 'Название строки в «Готовности данных»: агроциклы и работы' },
  readyCyclesFact: {
    id: 'district.overviewApi.ready.cyclesFact',
    defaultMessage:
      '{cycles}: {active, plural, one {# идёт} few {# идут} many {# идут} other {# идут}}, {closed, plural, one {# закрыт} few {# закрыты} many {# закрыты} other {# закрыты}}{planned, plural, =0 {} one {, # запланирован} few {, # запланированы} many {, # запланированы} other {, # запланированы}}; в активном агроцикле {works}',
    description: 'Факты строки «Агроциклы и работы»; {cycles} — «3 агроцикла», {active}/{closed}/{planned} — сколько идёт, закрыто, запланировано, {works} — «40 работ» или «работы не прочитаны»',
  },
  readyWorksUnread: { id: 'district.overviewApi.ready.worksUnread', defaultMessage: 'работы не прочитаны', description: 'Вместо числа работ активного агроцикла, когда работы не получены; строчными' },
  readyCyclesUnread: { id: 'district.overviewApi.ready.cyclesUnread', defaultMessage: 'список агроциклов не прочитан: {reason}', description: 'Факт строки «Агроциклы и работы», когда список не получен; {reason} — причина' },
  readyWater: { id: 'district.overviewApi.ready.water', defaultMessage: 'Вода активного агроцикла', description: 'Название строки в «Готовности данных»: вода активного агроцикла' },
  readyWaterFact: {
    id: 'district.overviewApi.ready.waterFact',
    defaultMessage: 'подано {delivered} из {requested}; охват: {noData} — основная база охват заявками не считает; заявок {requests}',
    description: 'Факты строки «Вода активного агроцикла»; {delivered}/{requested} — объёмы в м³ или «нет данных», {requests} — число заявок',
  },
  readyWaterUnread: { id: 'district.overviewApi.ready.waterUnread', defaultMessage: 'картина агроцикла не прочитана: {reason}', description: 'Факт строки «Вода активного агроцикла», когда картина не получена; {reason} — причина' },
  waterLoading: { id: 'district.overviewApi.ready.waterLoading', defaultMessage: 'загружается', description: 'Причина по умолчанию, почему не прочитана картина агроцикла: данные ещё грузятся; строчными' },
  waterNoCycle: { id: 'district.overviewApi.ready.waterNoCycle', defaultMessage: 'активного агроцикла нет', description: 'Причина по умолчанию, почему не прочитана картина агроцикла: нет идущего агроцикла; строчными' },
  readyQuarantine: { id: 'district.overviewApi.ready.quarantine', defaultMessage: 'Карантин', description: 'Название строки в «Готовности данных»: карантин' },
  readyQuarantineFact: {
    id: 'district.overviewApi.ready.quarantineFact',
    defaultMessage: 'красных и жёлтых {contours}; обследований: {inspections}; охват: {coverage}{note, select, none {} other { — {note}}}',
    description: 'Факты строки «Карантин»; {contours} — «20 контуров», {inspections} — число или «нет данных», {coverage} — число или «нет данных», {note} — пояснение сервера или none',
  },
  readyQuarantineUnread: { id: 'district.overviewApi.ready.quarantineUnread', defaultMessage: 'статусы карантина не прочитаны: {reason}', description: 'Факт строки «Карантин», когда данные не получены; {reason} — причина' },
});

export function OverviewApi() {
  const t = useT();
  const NO_DATA = noData();
  const { params } = useAnalysisContext();

  // Наборы прототипа — через адаптеры канона (источник маршрута `api`).
  const meta = useTrackData<Meta>(FILES.meta);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const crops = useTrackData<Crop[]>(FILES.crops);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const quarantine = useTrackData<QuarantineFile>(FILES.quarantine);

  // Статус цикла канона: «идёт» — только `active`; адаптер прототипа
  // складывает `planned` к незакрытым, и для выбора активного он не годится.
  const cycleRows = useRequest(() => api.seasonCycles(params), [params]);
  // Ход кампаний по массивам экран не читает — это ~1,4 МБ из ~1,6 МБ ответа (журнал №442).
  const overview = useRequest(() => api.districtOverview({ ...params, massif_progress: false }), [params]);
  const actorsPage = useRequest(() => api.actors({ ...params, limit: 1 }), [params]);

  const canonRows = useMemo(() => cycleRows.data?.items ?? [], [cycleRows.data]);
  const cycleList = useMemo(() => cycles.data ?? [], [cycles.data]);
  const byKey = useMemo(() => new Map(cycleList.map((c) => [c.id, c])), [cycleList]);
  const activeKey = canonRows.find((c) => c.status === 'active')?.cycle ?? null;
  const active = activeKey ? (byKey.get(activeKey) ?? null) : null;
  const closed = canonRows
    .filter((c) => c.status === 'closed')
    .map((c) => byKey.get(c.cycle))
    .filter((c): c is Cycle => Boolean(c));
  const planned = canonRows.filter((c) => c.status === 'planned').length;

  const works = useTrackData<Work[]>(active ? FILES.works(active.id) : null);
  const picture = useRequest(() => {
    if (!activeKey) return Promise.resolve(null);
    const query: QueryParams = { ...params, cycle: activeKey, summary: true, weeks: 0 };
    return once(`picture|${paramsKey(query)}`, () => api.seasonPicture(query));
  }, [params, activeKey]);

  const ov = overview.data;
  const pic = picture.data;
  const district = t(m.districtName);
  // Дата среза — общая (`as_of` полосы контекста); пустая — горизонт сервера.
  // Та же дата стоит на главной и в сводке к совещанию (agroai-0yt.2).
  const asOf = (typeof params.as_of === 'string' ? params.as_of : null) ?? pic?.horizon ?? ov?.horizon ?? meta.data?.as_of ?? null;
  const includeDemo = ov?.context.include_demo ?? Boolean(params.include_demo);

  usePageTrail([{ level: levelCaption('district'), name: district }]);

  const titleOf = (cycle: Cycle): string => cycleTitle(cycle, crops.data);

  const agriLand = ov ? landOf(ov.land, 'AGRICULTURAL_LAND_TOTAL') : null;
  const registry = useMemo(() => {
    const rows = ov?.massifs ?? [];
    return {
      contours: rows.reduce((s, m) => s + m.contours, 0),
      withGeometry: rows.reduce((s, m) => s + m.contours_with_geometry, 0),
    };
  }, [ov]);
  const actorsTotal = actorsPage.data?.page.total ?? null;

  const cropSlices = useMemo<CropSlice[]>(() => {
    const colorOf = new Map((crops.data ?? []).map((c) => [c.id, c.color]));
    return (ov?.crops.metrics ?? [])
      .filter((m) => m.level === 'crop' && m.area.value !== null)
      .map((m) => ({
        id: m.code,
        name: m.name ?? m.code,
        color: colorOf.get(m.code) ?? 'line',
        contours: null,
        area: microhaToHa(m.area.value) ?? 0,
      }))
      .sort((a, b) => b.area - a.area);
  }, [ov, crops.data]);
  const cropsNoArea = (ov?.crops.metrics ?? []).filter(
    (m) => m.level === 'crop' && m.area.value === null,
  ).length;
  const placedArea = cropSlices.reduce((s, c) => s + c.area, 0);

  const massifRows = useMemo<MassifRow[]>(
    () =>
      (ov?.massifs ?? [])
        .map((m) => ({
          id: String(m.massif_id),
          name: m.canonical_name,
          contours: m.contours,
          farms: m.actors,
          area: microhaToHa(m.total.value),
        }))
        .sort((a, b) => (b.area ?? -1) - (a.area ?? -1)),
    [ov],
  );
  const massifArea = massifRows.reduce((s, m) => s + (m.area ?? 0), 0);
  // Площадь, не названная сервером, в сумму не входит — и это говорится
  // словами: иначе частичная сумма читалась бы как полная.
  const massifsNoArea = massifRows.filter((m) => m.area == null).length;

  const queue = useMemo(
    () =>
      [...(ov?.attention ?? [])]
        .sort((a, b) => a.rank - b.rank)
        .map((item) => canonQueueItem(attentionLine(item))),
    [ov],
  );
  const queueTop = queue.slice(0, DENSITY.listItems);
  const queueRest = queue.slice(DENSITY.listItems);

  const activeWorks = works.data ?? [];
  const sliceMark = (
    <EvidenceMark
      kind={includeDemo ? 'demo' : 'fact'}
      label={includeDemo ? t(m.markDemo) : t(m.markFact)}
    />
  );
  const overviewReason = overview.error?.message ?? t(m.noReasonServer);
  const delivered = volumeM3(pic?.water.delivered_volume_m3);
  const requested = volumeM3(pic?.water.requested_volume_m3);

  return (
    <Stack>
      <PageHeader
        level="district"
        title={t(m.title, { district })}
        purpose={asOf ? t(m.purposeDate, { date: dateLong(asOf) }) : t(m.purposeNoDate)}
        actions={sliceMark}
      />

      {/* ------------------------------------------------------- 4 числа */}
      <div className="ov-kpis">
        <KpiCard
          label={t(m.kpiActive)}
          value={active ? ha(active.area_ha) : '—'}
          unit={active && Number.isFinite(active.area_ha) ? t(m.kpiHaLand) : undefined}
          icon={Sprout}
          variant="hero"
          note={
            active
              ? t(m.kpiActiveNote, { title: titleOf(active), stage: active.stage_now ?? t(m.stageUnnamed) })
              : cycles.state === 'loading' || cycleRows.state === 'loading'
                ? t(m.kpiActiveLoading)
                : t(m.kpiActiveNone)
          }
        />
        <KpiCard
          label={reportShareLabel()}
          value={pic ? reportShareValue(pic.obligations.reported, pic.obligations.total) : '—'}
          icon={ListChecks}
          note={
            pic
              ? reportShareNote(pic.obligations.reported, pic.obligations.total, asOf)
              : picture.state === 'loading'
                ? t(m.kpiPictureLoading)
                : t(m.kpiPictureUnread)
          }
        />
        <KpiCard
          label={t(m.kpiLand)}
          value={agriLand?.ha != null ? ha(agriLand.ha) : '—'}
          unit={agriLand?.ha != null ? t(m.kpiHa) : undefined}
          icon={LandPlot}
          note={
            ov
              ? agriLand?.ha != null
                ? t(m.kpiLandNote, { massifs: countOf(ov.massifs.length, WORDS.massif) })
                : t(m.kpiLandNoRow, { noData: NO_DATA })
              : overview.state === 'loading'
                ? t(m.kpiOverviewLoading)
                : t(m.kpiOverviewUnreadNumber)
          }
        />
        <KpiCard
          label={t(m.kpiFarms)}
          value={actorsTotal === null ? '—' : num(actorsTotal)}
          icon={Building2}
          note={
            actorsTotal === null
              ? actorsPage.state === 'loading'
                ? t(m.kpiFarmsLoading)
                : t(m.kpiFarmsUnread)
              : t(m.kpiFarmsNote)
          }
        />
        <KpiCard
          label={t(m.kpiContours)}
          value={ov ? num(registry.withGeometry) : '—'}
          icon={MapPin}
          note={
            ov
              ? t(m.kpiContoursNote, { count: registry.contours })
              : t(m.kpiOverviewUnread)
          }
        />
      </div>

      {/* ------------------------------------- цикл + очередь внимания */}
      <Grid cols="main-rail">
        <Stack tight>
          <Card
            size="lg"
            title={t(m.cardActive)}
            aside={
              activeWorks.length > 0 ? t(m.cardActiveAside, { works: countOf(activeWorks.length, WORDS.work) }) : null
            }
          >
            <DataGate res={cycles} what={t(m.gateActive)}>
              {() =>
                active ? (
                  <ActiveCycle
                    cycle={active}
                    title={titleOf(active)}
                    asOf={asOf}
                    worksRes={works}
                    obligations={pic ? pic.obligations : null}
                  />
                ) : (
                  <EmptyState
                    compact
                    icon={Sprout}
                    title={t(m.activeNoneTitle)}
                    reason={
                      cycleRows.state === 'error'
                        ? t(m.statusesUnread, { reason: cycleRows.error?.message ?? t(m.noReasonServer) })
                        : cycleRows.state === 'loading'
                          ? t(m.statusesLoading)
                          : t(m.activeNoneReason)
                    }
                  />
                )
              }
            </DataGate>
          </Card>

          <Card
            tone="soft"
            title={t(m.cardClosed)}
            aside={closed.length > 0 ? countOf(closed.length, WORDS.cycle) : null}
          >
            {closed.length === 0 ? (
              <EmptyState
                compact
                title={t(m.closedNoneTitle)}
                reason={
                  cycleRows.state === 'ready'
                    ? t(m.closedNoneReadyReason)
                    : t(m.closedNoneUnreadReason)
                }
              />
            ) : (
              <Stack tight>
                {closed.map((cycle) => (
                  <ClosedCycleRow
                    key={cycle.id}
                    cycleId={cycle.id}
                    title={titleOf(cycle)}
                    facts={closedCycleFacts(cycle)}
                  />
                ))}
              </Stack>
            )}
          </Card>
        </Stack>

        <Card
          title={t(m.cardQueue)}
          aside={
            queue.length > 0 ? t(m.queueAside, { top: num(queueTop.length), total: num(queue.length) }) : null
          }
        >
          {overview.state === 'loading' ? (
            <div className="t-caption">{t(m.queueLoading)}</div>
          ) : !ov ? (
            <EmptyState
              compact
              icon={ListChecks}
              title={t(m.queueUnreadTitle)}
              reason={t(m.queueUnreadReason, { reason: overviewReason })}
            />
          ) : queue.length === 0 ? (
            <EmptyState
              compact
              icon={ListChecks}
              title={t(m.queueEmptyTitle)}
              reason={t(m.queueEmptyReason)}
            />
          ) : (
            <Stack tight>
              <ul className="ov-queue">
                {queueTop.map((item) => (
                  <QueueItem item={item} key={item.key} />
                ))}
              </ul>
              <div className="ov-note">{t(m.queueNote, { noData: NO_DATA })}</div>
              {queueRest.length > 0 ? (
                <Disclosure
                  label={t(m.queueRest)}
                  summary={t(m.queueRestSummary, { count: queueRest.length })}
                >
                  <ul className="ov-queue">
                    {queueRest.map((item) => (
                      <QueueItem item={item} key={item.key} />
                    ))}
                  </ul>
                </Disclosure>
              ) : null}
            </Stack>
          )}
        </Card>
      </Grid>

      {/* ----------------------------------------------------- кадр карты */}
      <MapFrameCard
        crops={crops.data ?? undefined}
        massifs={massifs.data ?? undefined}
        geometryNote={
          !ov
            ? t(m.geoNone, { reason: overviewReason })
            : t(m.geoSome, { accepted: num(registry.withGeometry), total: registry.contours, noData: NO_DATA })
        }
        cropSlices={cropSlices}
        cropsEmptyNote={
          ov
            ? t(m.cropsMapEmpty)
            : t(m.cropsMapUnread, { reason: overviewReason })
        }
      />

      {/* --------------------------------------------------- раскрытия */}
      <Stack tight>
        <Disclosure
          label={t(m.discCrops)}
          summary={
            ov
              ? t(m.discCropsSummary, { crops: countOf(cropSlices.length, WORDS.crop), ha: ha(placedArea) })
              : t(m.discUnread)
          }
        >
          {!ov ? (
            <EmptyState
              compact
              title={t(m.cropsUnreadTitle)}
              reason={t(m.cropsUnreadReason, { reason: overviewReason })}
            />
          ) : (
            <CropsBreakdown
              placed={cropSlices}
              emptyReason={t(m.cropsEmptyReason)}
              contoursNote={t(m.cropsContoursNote, { noData: NO_DATA, season: String(ov.season) })}
              unplacedNote={
                [
                  cropsNoArea > 0
                    ? t(m.cropsNoArea, { crops: countOf(cropsNoArea, WORDS.crop) })
                    : null,
                  ov.crops.mismatch_placements > 0
                    ? t(m.cropsMismatch, {
                      count: num(ov.crops.mismatch_placements),
                      diff: haUnit(microhaToHa(ov.crops.mismatch_microha)),
                    })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' ') || null
              }
            />
          )}
        </Disclosure>

        <Disclosure
          label={t(m.discMassifs)}
          summary={
            massifRows.length > 0
              ? massifsNoArea > 0
                ? t(m.discMassifsSummaryNoArea, { massifs: countOf(massifRows.length, WORDS.massif), ha: ha(massifArea), missing: num(massifsNoArea) })
                : t(m.discMassifsSummary, { massifs: countOf(massifRows.length, WORDS.massif), ha: ha(massifArea) })
              : t(m.discListUnread)
          }
        >
          <MassifTable
            rows={massifRows}
            emptyReason={
              ov
                ? t(m.massifsEmptyReady)
                : t(m.massifsEmptyUnread, { reason: overviewReason })
            }
          />
        </Disclosure>

        <Disclosure
          label={t(m.discReady)}
          summary={
            ov
              ? t(m.discReadySummary, { accepted: num(registry.withGeometry), total: num(registry.contours) })
              : t(m.discCountUnknown)
          }
        >
          <div className="ov-ready">
            <ReadyRow
              name={t(m.readyRegistry)}
              fact={
                ov
                  ? t(m.readyRegistryFact, {
                    contours: countOf(registry.contours, WORDS.contour),
                    area: agriLand?.ha != null ? haUnit(agriLand.ha) : t(m.readyLandNoData, { noData: NO_DATA }),
                    massifs: countOf(ov.massifs.length, WORDS.massif),
                  })
                  : t(m.readyOverviewUnread, { reason: overviewReason })
              }
              mark={ov ? <EvidenceMark kind="fact" label={t(m.markCanon)} /> : null}
            />

            <ReadyRow
              name={t(m.readyGeometry)}
              fact={
                ov
                  ? t(m.readyGeometryFact, {
                    accepted: num(registry.withGeometry),
                    total: num(registry.contours),
                    noData: NO_DATA,
                    none: num(registry.contours - registry.withGeometry),
                  })
                  : t(m.readyOverviewUnread, { reason: overviewReason })
              }
              mark={ov ? <EvidenceMark kind="derived" label={t(m.markDerived)} /> : null}
            />

            <ReadyRow
              name={t(m.readyFarms)}
              fact={
                actorsTotal !== null
                  ? t(m.readyFarmsFact, { farms: countOf(actorsTotal, WORDS.farm), noData: NO_DATA })
                  : t(m.readyFarmsUnread)
              }
              mark={actorsTotal !== null ? <EvidenceMark kind="fact" label={t(m.markCanon)} /> : null}
            />

            <ReadyRow
              name={t(m.readyCycles)}
              fact={
                canonRows.length > 0
                  ? t(m.readyCyclesFact, {
                    cycles: countOf(canonRows.length, WORDS.cycle),
                    active: canonRows.filter((c) => c.status === 'active').length,
                    closed: closed.length,
                    planned,
                    works: activeWorks.length > 0 ? countOf(activeWorks.length, WORDS.work) : t(m.readyWorksUnread),
                  })
                  : t(m.readyCyclesUnread, { reason: cycleRows.error?.message ?? t(COMMON.noReason) })
              }
              mark={canonRows.length > 0 ? sliceMark : null}
            />

            <ReadyRow
              name={t(m.readyWater)}
              fact={
                pic
                  ? t(m.readyWaterFact, {
                    delivered: delivered === null ? NO_DATA : m3(delivered),
                    requested: requested === null ? NO_DATA : m3(requested),
                    noData: NO_DATA,
                    requests: num(pic.water.requests),
                  })
                  : t(m.readyWaterUnread, {
                    reason: picture.error?.message ?? (activeKey ? t(m.waterLoading) : t(m.waterNoCycle)),
                  })
              }
              mark={pic ? sliceMark : null}
            />

            <ReadyRow
              name={t(m.readyQuarantine)}
              fact={
                quarantine.data
                  ? t(m.readyQuarantineFact, {
                    contours: countOf(quarantine.data.summary.contours_watch, WORDS.contour),
                    inspections: quarantine.data.summary.inspections === null ? NO_DATA : num(quarantine.data.summary.inspections),
                    coverage: quarantine.data.summary.coverage_pct === null ? NO_DATA : num(quarantine.data.summary.coverage_pct),
                    note: quarantine.data.inspections_note || 'none',
                  })
                  : t(m.readyQuarantineUnread, { reason: quarantine.reason ?? t(COMMON.noReason) })
              }
              mark={quarantine.data ? <EvidenceMark kind="fact" label={t(m.markCanon)} /> : null}
            />

            {(ov?.gaps ?? []).map((gap) => (
              <ReadyRow key={gap.key} name={gap.title} fact={gap.reason} />
            ))}
          </div>
        </Disclosure>
      </Stack>
    </Stack>
  );
}

export default OverviewApi;
